import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useToast } from '../../lib/toast';
import { Drawer, drawerStyles } from '../../components/Drawer';
import { IntakeStatusBadge } from '../../components/StatusBadge';
import { formatShortDate } from '../../lib/format';
import type { IntakeSubmission } from '../../lib/types';
import styles from './Dashboard.module.css';

const FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'pending_review', label: 'Pending Review' },
  { key: 'ready_for_contract', label: 'Ready for Contract' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'pending_signature', label: 'Pending Signature' },
  { key: 'contract_signed', label: 'Contract Signed' },
];

const CONTRACT_TAB_STATUSES = [
  'ready_for_contract',
  'pending_signature',
  'contract_rejected',
  'contract_signed',
];

const STATUS_TINT: Record<string, string> = {
  pending_review: styles.iPending,
  ready_for_contract: styles.iReady,
  rejected: styles.iRejected,
  pending_signature: styles.iPendingSig,
  contract_rejected: styles.iContractRejected,
  contract_signed: styles.iContractSigned,
};

function platformSummary(handles: { platformName: string }[] | undefined): string {
  if (!handles || handles.length === 0) return '—';
  const names = handles.map((h) => h.platformName).filter(Boolean);
  if (names.length === 0) return '—';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

function productLabel(s: IntakeSubmission['productInterest']): string {
  const arr = Array.isArray(s) ? s : s ? [s] : [];
  return arr
    .filter(Boolean)
    .map((p) =>
      typeof p === 'string'
        ? p.includes('Beta Blocker')
          ? 'Beta Blockers'
          : p.includes('Sleep')
            ? 'Sleep Plan'
            : p
        : '',
    )
    .join(', ');
}

export function IntakeSubmissionsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['admin', 'applications'],
    queryFn: () => api.get<IntakeSubmission[]>('/api/admin/applications'),
  });
  const subs = data ?? [];
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tab, setTab] = useState<'details' | 'contract'>('details');

  // Drawer editable state
  const [reviewedFinn, setReviewedFinn] = useState(false);
  const [reviewedDan, setReviewedDan] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');

  const filtered = useMemo(
    () => (filter ? subs.filter((s) => s.status === filter) : subs),
    [subs, filter],
  );
  const selected = useMemo(
    () => subs.find((s) => s.id === selectedId) ?? null,
    [subs, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setReviewedFinn(!!selected.reviewedByFinn);
      setReviewedDan(!!selected.reviewedByDan);
      setInternalNotes(selected.internalNotes || '');
      setTab('details');
    }
  }, [selected]);

  const patch = useMutation({
    mutationFn: (vars: { id: number; body: Record<string, unknown> }) =>
      api.patch<IntakeSubmission>(`/api/admin/applications/${vars.id}`, vars.body),
    onSuccess: (updated) => {
      qc.setQueryData<IntakeSubmission[]>(['admin', 'applications'], (prev) =>
        prev?.map((s) => (s.id === updated.id ? updated : s)) ?? [],
      );
    },
  });

  function saveReview() {
    if (!selectedId) return;
    patch.mutate(
      {
        id: selectedId,
        body: { reviewedByFinn: reviewedFinn, reviewedByDan: reviewedDan, internalNotes },
      },
      { onSuccess: () => toast('Saved'), onError: () => toast('Error saving') },
    );
  }

  function decide(status: 'ready_for_contract' | 'rejected') {
    if (!selectedId) return;
    patch.mutate(
      { id: selectedId, body: { status } },
      {
        onSuccess: () =>
          toast(status === 'ready_for_contract' ? '✓ Approved — onboarding email sent' : '✗ Rejected'),
        onError: () => toast('Error updating status'),
      },
    );
  }

  const bothReviewed = reviewedFinn && reviewedDan;
  const showContractTab = selected && CONTRACT_TAB_STATUSES.includes(selected.status);

  return (
    <>
      <div className={styles.intakeTopbar}>
        <div className={styles.pillRow}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.iPill} ${filter === f.key ? styles.iPillActive : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className={styles.intakeTotal}>
          {filtered.length} submission{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className={styles.tableWrap}>
        {filtered.length === 0 ? (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>📋</div>
            <div>No intake submissions yet</div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Platform</th>
                <th>Products</th>
                <th>Reviews</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  className={`${selectedId === s.id ? styles.selected : ''} ${STATUS_TINT[s.status] ?? ''}`}
                  onClick={() => setSelectedId(s.id)}
                >
                  <td>
                    <strong>{s.fullName || '—'}</strong>
                  </td>
                  <td className={styles.tdSmall}>{s.email || '—'}</td>
                  <td>{platformSummary(s.platformHandles)}</td>
                  <td className={styles.tdSmall}>{productLabel(s.productInterest) || '—'}</td>
                  <td>
                    <div className={styles.rdots}>
                      <span
                        className={`${styles.rdot} ${s.reviewedByFinn ? styles.rdotDone : ''}`}
                        title="Finn"
                      />
                      <span
                        className={`${styles.rdot} ${s.reviewedByDan ? styles.rdotDone : ''}`}
                        title="Dan"
                      />
                    </div>
                  </td>
                  <td>
                    <IntakeStatusBadge status={s.status} />
                  </td>
                  <td className={styles.tdUpdated}>{formatShortDate(s.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        wide
        header={
          <div className={drawerStyles.header}>
            <button
              type="button"
              className={drawerStyles.closeBtn}
              onClick={() => setSelectedId(null)}
            >
              ← Back
            </button>
            <div className={drawerStyles.actions}>
              <button
                type="button"
                className={drawerStyles.saveBtn}
                onClick={saveReview}
                disabled={patch.isPending}
              >
                {patch.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        }
      >
        {selected && (
          <>
            <div className={drawerStyles.tabBar}>
              <button
                type="button"
                className={`${drawerStyles.tab} ${tab === 'details' ? drawerStyles.tabActive : ''}`}
                onClick={() => setTab('details')}
              >
                Details
              </button>
              {showContractTab && (
                <button
                  type="button"
                  className={`${drawerStyles.tab} ${tab === 'contract' ? drawerStyles.tabActive : ''}`}
                  onClick={() => setTab('contract')}
                >
                  Contract
                </button>
              )}
            </div>

            {tab === 'details' && (
              <>
                <div className={drawerStyles.titleBlock}>
                  <h2>{selected.fullName || '—'}</h2>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
                    {selected.email || '—'}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <IntakeStatusBadge status={selected.status} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                    Submitted {formatShortDate(selected.submittedAt)}
                  </div>
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Platform &amp; Social</div>
                  <div className={styles.fgrid}>
                    <div className={styles.fitem}>
                      <label>Platform</label>
                      <div className={drawerStyles.readonly}>
                        {selected.platformHandles?.[0]?.platformName ?? '—'}
                      </div>
                    </div>
                    <div className={`${styles.fitem} ${styles.fitemFull}`}>
                      <label>Handles &amp; Follower Counts</label>
                      <div className={drawerStyles.readonly}>
                        {selected.platformHandles?.length ? (
                          selected.platformHandles.map((h) => (
                            <div key={h.id}>
                              <strong>{h.platformName}</strong>
                              {h.handle ? ` @${h.handle}` : ''}
                              {h.followerCount > 0
                                ? ` — ${h.followerCount.toLocaleString()} followers`
                                : ''}
                              {h.platformLink && (
                                <a
                                  href={h.platformLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'var(--text)', marginLeft: 6 }}
                                >
                                  ↗
                                </a>
                              )}
                            </div>
                          ))
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                    <div className={`${styles.fitem} ${styles.fitemFull}`}>
                      <label>Main Account Link</label>
                      <div className={drawerStyles.readonly}>
                        {selected.mainLink ? (
                          <a href={selected.mainLink} target="_blank" rel="noreferrer">
                            {selected.mainLink}
                          </a>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Content</div>
                  <div className={`${styles.fitem} ${styles.fitemFull}`} style={{ marginBottom: 12 }}>
                    <label>Product Interest</label>
                    <div className={drawerStyles.readonly}>
                      {productLabel(selected.productInterest) || '—'}
                    </div>
                  </div>
                  <div className={`${styles.fitem} ${styles.fitemFull}`} style={{ marginBottom: 12 }}>
                    <label>Content Description</label>
                    <div className={drawerStyles.readonly}>
                      {selected.contentDescription || '—'}
                    </div>
                  </div>
                  <div className={`${styles.fitem} ${styles.fitemFull}`} style={{ marginBottom: 12 }}>
                    <label>Audience Description</label>
                    <div className={drawerStyles.readonly}>
                      {selected.audienceDescription || '—'}
                    </div>
                  </div>
                  <div className={`${styles.fitem} ${styles.fitemFull}`}>
                    <label>Video Idea</label>
                    <div className={drawerStyles.readonly}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {selected.hasIdeas === 'yes'
                          ? 'Has their own concept'
                          : selected.hasIdeas === 'no'
                            ? 'Needs help with concepts'
                            : '—'}
                      </div>
                      {selected.hasIdeas === 'yes' && (selected.ideaDetail || '—')}
                      {selected.hasIdeas === 'no' && (selected.helpDetail || '—')}
                    </div>
                  </div>
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Payment</div>
                  <div className={styles.fgrid}>
                    <div className={`${styles.fitem} ${styles.fitemFull}`}>
                      <label>PayPal Email</label>
                      <div className={drawerStyles.readonly}>
                        {selected.paypalEmail || '—'}
                      </div>
                    </div>
                    <div className={styles.fitem}>
                      <label>Recipient Name</label>
                      <div className={drawerStyles.readonly}>
                        {selected.paypalRecipientName || '—'}
                      </div>
                    </div>
                    <div className={styles.fitem}>
                      <label>Phone</label>
                      <div className={drawerStyles.readonly}>
                        {selected.paypalPhone || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Agreement Confirmations</div>
                  <div className={styles.agrStack}>
                    {(
                      [
                        [selected.agreedRate, 'Agreed to $300 flat rate'],
                        [selected.agreedRevisions, 'Agreed to revision process'],
                        [selected.agreedIntake, 'Agreed to online intake (~15 min)'],
                      ] as [boolean, string][]
                    ).map(([val, label], i) => (
                      <div
                        key={i}
                        className={`${styles.agrRow} ${val ? styles.agrYes : styles.agrNo}`}
                      >
                        <span className={styles.agrIcon}>{val ? '✓' : '✗'}</span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {selected.extraNotes && (
                  <div className={drawerStyles.section}>
                    <div className={drawerStyles.sectionLabel}>Creator Notes</div>
                    <div className={drawerStyles.readonly}>{selected.extraNotes}</div>
                  </div>
                )}

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Internal Review</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label
                      className={`${styles.reviewCheck} ${reviewedFinn ? styles.rciChecked : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={reviewedFinn}
                        onChange={(e) => setReviewedFinn(e.target.checked)}
                      />
                      <span className={styles.rciName}>Reviewed by Finn</span>
                    </label>
                    <label
                      className={`${styles.reviewCheck} ${reviewedDan ? styles.rciChecked : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={reviewedDan}
                        onChange={(e) => setReviewedDan(e.target.checked)}
                      />
                      <span className={styles.rciName}>Reviewed by Dan</span>
                    </label>
                  </div>
                  <div className={styles.reviewProgress}>
                    {(reviewedFinn ? 1 : 0) + (reviewedDan ? 1 : 0)} of 2 reviews complete
                  </div>
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Internal Notes</div>
                  <textarea
                    className={styles.textarea}
                    rows={5}
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Add notes for the team…"
                  />
                </div>

                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>Decision</div>
                  <div className={styles.statusBtns}>
                    <button
                      type="button"
                      className={`${styles.statusBtn} ${selected.status === 'ready_for_contract' ? styles.activeApprove : ''}`}
                      disabled={!bothReviewed}
                      onClick={() => decide('ready_for_contract')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className={`${styles.statusBtn} ${selected.status === 'rejected' ? styles.activeReject : ''}`}
                      disabled={!bothReviewed}
                      onClick={() => decide('rejected')}
                    >
                      Reject
                    </button>
                  </div>
                  {!bothReviewed && (
                    <div className={styles.reviewGateNote}>
                      Both Finn and Dan must review before approving.
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === 'contract' && (
              <>
                <div className={drawerStyles.titleBlock}>
                  <h2>{selected.fullName || '—'}</h2>
                  <div style={{ marginTop: 8 }}>
                    <IntakeStatusBadge status={selected.status} />
                  </div>
                </div>
                <div className={drawerStyles.section}>
                  <div className={drawerStyles.sectionLabel}>DocuSign</div>
                  <div className={styles.contractSendBlock}>
                    <div className={styles.contractSendIcon}>📄</div>
                    <p className={styles.contractSendDesc}>
                      Send a partnership agreement to the creator via DocuSign.
                    </p>
                    <button type="button" className={styles.btnDocusign} disabled>
                      Send DocuSign
                    </button>
                    <p className={styles.contractTodoNote}>
                      🛠 Backend integration pending — DocuSign API not yet connected
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
